// Ingestion filters and grouping for Sentry (JURYBOX-WEB-APP-18, -19; and the
// key-init family JURYBOX-WEB-APP-K, -X, -13, -1B, and -17 on the unwrap
// path).
//
// Browser extensions inject their scripts into the page and their failures
// surface through the very same global handlers the app installs, so an
// extension's bugs arrive in Jurybox's Sentry project looking like ours.
// Nothing in this codebase can fix a third party's extension, and the volume
// crowds real signal out of triage: a single PayPal Honey install produced
// three new issue groups in one page load.
//
// Two distinct shapes need two distinct filters, because they arrive with
// different amounts of evidence:
//
//   1. The extension's script IS the top stack frame — a file inside the
//      extension bundle. `denyUrls` matches on that frame, so
//      EXTENSION_URL_PATTERNS handles this case.
//   2. There is no stack frame at all. An extension dispatches a synthetic
//      `unhandledrejection` at `window`, and the SDK captures the DOM Event
//      object itself as the "rejection reason". `denyUrls` cannot see these —
//      there is no URL to match — so isDomEventRejection catches them by the
//      shape of the captured value instead.

import isUnreachableError from './isUnreachableError';

/**
 * URL fragments that identify a browser-extension script rather than app code.
 *
 * Covers the `*-extension://` schemes, plus the two on-disk layouts Safari
 * uses for extensions shipped inside a native app bundle
 * (`.safariextension/` resource directories and `Extension.appex/` App
 * Extension bundles) — the latter is what JURYBOX-WEB-APP-18 reports.
 *
 * On the scheme list: Chromium browsers (Chrome, Edge, Brave, Opera) all use
 * `chrome-extension://`, so that one entry covers Edge too.
 * `ms-browser-extension://` is legacy EdgeHTML only — kept because it costs
 * nothing, not because modern Edge needs it.
 */
export const EXTENSION_URL_PATTERNS = [
  /^(?:chrome|chrome-untrusted|moz|safari|safari-web|ms-browser)-extension:\/\//i,
  /\.safariextension\//i,
  /\/Extension\.appex\//i,
];

/**
 * True when the value a promise rejected with is a DOM Event.
 *
 * No first-party code here rejects with one: every `reject()` call site in
 * `src` (`fetchWithTimeout`, `asyncActions`, `WebSocketClient`, `Root.jsx`)
 * passes an Error or a caught exception, and Dexie/IndexedDB and Web Crypto
 * failures surface as `DOMException`, which is not an `Event`. What we see
 * instead is an extension re-dispatching a synthetic `unhandledrejection` at
 * the window, which the SDK's global handler captures verbatim.
 *
 * Scope worth being honest about: that enumeration covers `src`, not our
 * dependencies. A third-party library that rejected with a `ProgressEvent`
 * from a storage or IDB handler would be dropped here too. That is accepted
 * rather than overlooked — such a rejection carries no stack, no message and
 * no app frames, which is the same reason this filter exists at all — but it
 * is a wider net than "app code never does this" alone would justify.
 *
 * @param {{originalException?: unknown}} [hint] - Sentry's beforeSend hint
 * @returns {boolean} true when the event should be dropped
 */
export function isDomEventRejection(hint) {
  if (typeof Event === 'undefined') {
    return false;
  }
  return hint?.originalException instanceof Event;
}

/**
 * Stable grouping key for "the server could not be reached while unlocking
 * local data" — the failure behind JURYBOX-WEB-APP-K, -X, -13 and -1B.
 *
 * Those four issues are one failure. Sentry groups a thrown Error by the stack
 * frame that threw it, and this Error is constructed inside the `getKeyStatus`
 * catch in `authContext`, so every refactor that moves that `throw` opens a
 * brand-new issue group:
 *
 *   | Issue      | Culprit | Line | Events / users |
 *   |------------|---------|------|----------------|
 *   | WEB-APP-K  | `mc`    | 101  | 24 / 23        |
 *   | WEB-APP-X  | `nl`    |  --  |  3 / 3         |
 *   | WEB-APP-13 | `rl`    | 291  | 10 / 9         |
 *   | WEB-APP-1B | `pc`    | 337  |  1 / 1         |
 *
 * Together that is 38 events across ~36 users, the largest error class in this
 * app. Split four ways it reads as a one-event curiosity: an `is:unresolved`
 * triage query sees only the newest fragment, and because three of the four are
 * already adjudicated and closed, a recurrence lands in a *different* group than
 * the one that was resolved, so Sentry's regression detection never fires. The
 * splitting also hides its own scale: this family was triaged as three groups
 * totalling 16 events the day before WEB-APP-X was found.
 *
 * compliance-log#223 tracks the underlying transport failure, whose cause is
 * still unknown and which this does not attempt to fix. The fingerprint is that
 * issue's own first recommended next step: without it, the next refactor of this
 * path mints a fifth group and starts the count over again.
 *
 * `authContext`'s own docblock asserts that translating the message at the
 * display site "keeps the raw error intact for `isUnreachableError`, Sentry
 * grouping and the linked-error trail". That is true of the message, but not of
 * the grouping: #228 moved the throw site and WEB-APP-1B opened five weeks
 * later. An explicit fingerprint is what actually decouples the two.
 *
 * Deliberately narrow on both axes:
 *
 * - It requires the `app_init_stage: 'encryption-key'` tag, which this app sets
 *   on purpose at the single call site that reports init failures, rather than
 *   matching message text — that text varies with HTTP status and has already
 *   been rewritten once (#228).
 * - It requires `isUnreachableError`, so a request the server actually answered
 *   keeps its default grouping. A 500 from `/keys/unwrap` and a dropped
 *   connection need different triage, and merging them would trade one bad
 *   grouping for another.
 *
 * Wider than those four issues, though, and deliberately so: the gate is the
 * whole `encryption-key` stage, not the `getKeyStatus` catch. That stage has a
 * single init-failure capture site, and its own comment describes what reaches
 * it as "server unreachable, DEK unwrap/wrap failure" — so a transport timeout
 * propagated raw from `unlockDek`/`serverWrapDek` is tagged and matched here
 * too. JURYBOX-WEB-APP-17 (25 s on `/api/keys/unwrap`) is exactly that shape
 * and will join this group.
 *
 * That is the right grain rather than an accident. WEB-APP-17 and WEB-APP-13
 * are the *same* 47-second connectivity episode — same user, same trace
 * (`074fc007…`), 13 seconds apart — reaching the user through the unwrap path
 * and the status path respectively. "Could not reach the server during key
 * init" is one operational problem however it surfaces. Stated plainly so the
 * next reader is not surprised to find an unwrap timeout in a group the table
 * above does not list.
 *
 * No `'{{ default }}'` element, by decision rather than omission: appending
 * Sentry's default contribution would re-split this group by stack frame,
 * which is the precise behaviour the fingerprint exists to replace. Every
 * matching event therefore collapses into one group across releases and throw
 * sites.
 */
export const KEY_INIT_UNREACHABLE_FINGERPRINT = Object.freeze([
  'key-init',
  'server-unreachable',
]);

/**
 * The fingerprint for an event, or null to leave Sentry's grouping alone.
 *
 * @param {object} event - the Sentry event
 * @param {object} [hint] - the accompanying hint, carrying originalException
 * @returns {string[]|null} the fingerprint to apply, or null
 */
export function keyInitFingerprint(event, hint) {
  if (event?.tags?.app_init_stage !== 'encryption-key') {
    return null;
  }
  if (!isUnreachableError(hint?.originalException)) {
    return null;
  }
  return [...KEY_INIT_UNREACHABLE_FINGERPRINT];
}

/**
 * Sentry `beforeSend` hook: drop known-unactionable third-party noise, and hold
 * one recurring failure in a single issue group across refactors.
 *
 * Kept deliberately narrow — it only discards events this app provably cannot
 * produce. Anything ambiguous is left to reach Sentry, where it can be triaged
 * against real evidence rather than silently dropped at the client.
 *
 * @param {object} event - the Sentry event
 * @param {object} [hint] - the accompanying hint, carrying originalException
 * @returns {object|null} the event to send, or null to drop it
 */
export function beforeSend(event, hint) {
  if (isDomEventRejection(hint)) {
    return null;
  }
  const fingerprint = keyInitFingerprint(event, hint);
  if (fingerprint) {
    event.fingerprint = fingerprint;
  }
  return event;
}
